import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import "./sip-calculator.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const formatINR = (amount) =>
  Number(amount.toFixed(0)).toLocaleString("en-IN");

const LIFE_EXPECTANCY = 85;

function computeRetirement({
  currentAge,
  currentMonthlyExpenses,
  currentInvestments,
  returnRateBeforeRetirement,
  retirementAge,
  returnForCurrentInvestments,
  inflationPostRetirement,
}) {
  const yearsToRetirement = retirementAge - currentAge;
  if (
    yearsToRetirement <= 0 ||
    currentMonthlyExpenses <= 0 ||
    retirementAge <= 0 ||
    currentAge <= 0
  ) {
    return null;
  }

  const monthsToRetirement = yearsToRetirement * 12;

  const annualExpensesAtRetirement =
    currentMonthlyExpenses *
    12 *
    Math.pow(1 + inflationPostRetirement / 100, yearsToRetirement);

  const futureValueInvestments =
    currentInvestments *
    Math.pow(1 + returnForCurrentInvestments / 100, yearsToRetirement);

  const retirementYears = LIFE_EXPECTANCY - retirementAge;
  if (retirementYears <= 0) return null;

  const requiredCorpus = annualExpensesAtRetirement * retirementYears;
  const corpusToAccumulate = Math.max(
    requiredCorpus - futureValueInvestments,
    0
  );

  const monthlyRate = returnRateBeforeRetirement / 100 / 12;
  let monthlyInvestment = 0;
  if (monthsToRetirement > 0) {
    if (monthlyRate === 0) {
      monthlyInvestment = corpusToAccumulate / monthsToRetirement;
    } else {
      monthlyInvestment =
        (corpusToAccumulate * monthlyRate) /
        (Math.pow(1 + monthlyRate, monthsToRetirement) - 1);
    }
  }

  const growthData = [];
  for (let y = 1; y <= yearsToRetirement; y++) {
    const months = y * 12;
    const investedCumulative = monthlyInvestment * months;
    let returns = 0;
    if (monthlyRate === 0) {
      returns = 0;
    } else {
      returns =
        monthlyInvestment *
          ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) -
        investedCumulative;
    }
    growthData.push({
      year: y,
      invested: Number(investedCumulative.toFixed(0)),
      returns: Number(returns.toFixed(0)),
    });
  }

  return {
    yearsToRetirement,
    futureValueInvestments: Number(futureValueInvestments.toFixed(0)),
    corpusToAccumulate: Number(corpusToAccumulate.toFixed(0)),
    requiredCorpus: Number(requiredCorpus.toFixed(0)),
    monthlyInvestment: Number(monthlyInvestment.toFixed(0)),
    growthData,
  };
}

function NumberField({
  id,
  label,
  value,
  onChange,
  prefix,
  suffix,
  min,
  max,
  step = 1,
  wide,
}) {
  return (
    <div className="retire-field">
      <label htmlFor={id}>{label}</label>
      <div
        className={`sip-field-value${wide ? " sip-field-value--wide" : ""}`}
      >
        {prefix ? <span>{prefix}</span> : null}
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
        />
        {suffix ? <span>{suffix}</span> : null}
      </div>
    </div>
  );
}

export default function RetirementLumpsumCalculator() {
  const [currentAge, setCurrentAge] = useState(35);
  const [currentMonthlyExpenses, setCurrentMonthlyExpenses] = useState(50000);
  const [currentInvestments, setCurrentInvestments] = useState(1000000);
  const [inflationBeforeRetirement, setInflationBeforeRetirement] = useState(7);
  const [returnRateBeforeRetirement, setReturnRateBeforeRetirement] =
    useState(12);
  const [retirementAge, setRetirementAge] = useState(60);
  const [otherAnnualExpenses, setOtherAnnualExpenses] = useState(100000);
  const [returnForCurrentInvestments, setReturnForCurrentInvestments] =
    useState(14);
  const [inflationPostRetirement, setInflationPostRetirement] = useState(6);
  const [returnRateAfterRetirement, setReturnRateAfterRetirement] = useState(8);
  const [incomePercentage, setIncomePercentage] = useState(10);

  const results = useMemo(
    () =>
      computeRetirement({
        currentAge,
        currentMonthlyExpenses,
        currentInvestments,
        returnRateBeforeRetirement,
        retirementAge,
        returnForCurrentInvestments,
        inflationPostRetirement,
      }),
    [
      currentAge,
      currentMonthlyExpenses,
      currentInvestments,
      returnRateBeforeRetirement,
      retirementAge,
      returnForCurrentInvestments,
      inflationPostRetirement,
    ]
  );

  const doughnutData = results && {
    labels: ["Current Investment Value", "Corpus to Accumulate"],
    datasets: [
      {
        data: [results.futureValueInvestments, results.corpusToAccumulate],
        backgroundColor: ["#139bb2", "#a8e4ec"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${formatINR(ctx.parsed)}`,
        },
      },
    },
  };

  const barData = results && {
    labels: results.growthData.map((d) => `Y${d.year}`),
    datasets: [
      {
        label: "Invested",
        data: results.growthData.map((d) => d.invested),
        backgroundColor: "#139bb2",
        borderRadius: 4,
        stack: "growth",
      },
      {
        label: "Returns",
        data: results.growthData.map((d) => d.returns),
        backgroundColor: "#a8e4ec",
        borderRadius: 4,
        stack: "growth",
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ₹${formatINR(ctx.parsed.y)}`,
        },
      },
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: "#64748b",
          font: { size: 12, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: "#94a3b8", font: { size: 11 } },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "#eef2f5" },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          callback: (v) => `₹${formatINR(v)}`,
        },
      },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div className="sip-page">
      <section className="sip-hero">
        <div className="sip-hero-blob" aria-hidden="true" />
        <div className="sip-hero-inner">
          <p className="sip-eyebrow">RETIREMENT CALCULATOR</p>
          <h1 className="sip-hero-title">
            Plan the Corpus You Need for a <span>Comfortable Retirement</span>
          </h1>
          <p className="sip-hero-lead">
            Estimate your required retirement corpus and the monthly investment
            needed to get there, based on your age, expenses, and existing
            investments.
          </p>
        </div>
      </section>

      <section className="sip-calc">
        <div className="sip-calc-inner">
          <div className="sip-calc-grid retire-grid">
            <div className="sip-panel">
              <h2 className="sip-panel-title">Your Details</h2>

              <div className="retire-group">
                <h3 className="retire-group-title">Age &amp; Timeline</h3>
                <div className="retire-fields">
                  <NumberField
                    id="retire-current-age"
                    label="Current Age"
                    value={currentAge}
                    onChange={setCurrentAge}
                    suffix="Yr"
                    min={18}
                    max={70}
                  />
                  <NumberField
                    id="retire-age"
                    label="Retirement Age"
                    value={retirementAge}
                    onChange={setRetirementAge}
                    suffix="Yr"
                    min={40}
                    max={75}
                  />
                </div>
              </div>

              <div className="retire-group">
                <h3 className="retire-group-title">Expenses &amp; Savings</h3>
                <div className="retire-fields">
                  <NumberField
                    id="retire-expenses"
                    label="Monthly Expenses"
                    value={currentMonthlyExpenses}
                    onChange={setCurrentMonthlyExpenses}
                    prefix="₹"
                    min={5000}
                    max={1000000}
                    step={1000}
                    wide
                  />
                  <NumberField
                    id="retire-investments"
                    label="Current Investments"
                    value={currentInvestments}
                    onChange={setCurrentInvestments}
                    prefix="₹"
                    min={0}
                    max={100000000}
                    step={10000}
                    wide
                  />
                  <NumberField
                    id="retire-other-exp"
                    label="Other Annual Expenses"
                    value={otherAnnualExpenses}
                    onChange={setOtherAnnualExpenses}
                    prefix="₹"
                    min={0}
                    max={10000000}
                    step={10000}
                    wide
                  />
                  <NumberField
                    id="retire-income-pct"
                    label="Income for Simulation"
                    value={incomePercentage}
                    onChange={setIncomePercentage}
                    suffix="%"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
              </div>

              <div className="retire-group">
                <h3 className="retire-group-title">Returns &amp; Inflation</h3>
                <div className="retire-fields">
                  <NumberField
                    id="retire-infl-pre"
                    label="Inflation Before Retirement"
                    value={inflationBeforeRetirement}
                    onChange={setInflationBeforeRetirement}
                    suffix="%"
                    min={1}
                    max={15}
                    step={0.5}
                  />
                  <NumberField
                    id="retire-infl-post"
                    label="Inflation After Retirement"
                    value={inflationPostRetirement}
                    onChange={setInflationPostRetirement}
                    suffix="%"
                    min={1}
                    max={15}
                    step={0.5}
                  />
                  <NumberField
                    id="retire-return-pre"
                    label="Return Before Retirement"
                    value={returnRateBeforeRetirement}
                    onChange={setReturnRateBeforeRetirement}
                    suffix="%"
                    min={1}
                    max={20}
                    step={0.5}
                  />
                  <NumberField
                    id="retire-return-post"
                    label="Return After Retirement"
                    value={returnRateAfterRetirement}
                    onChange={setReturnRateAfterRetirement}
                    suffix="%"
                    min={1}
                    max={15}
                    step={0.5}
                  />
                  <NumberField
                    id="retire-return-current"
                    label="Return on Current Investments"
                    value={returnForCurrentInvestments}
                    onChange={setReturnForCurrentInvestments}
                    suffix="%"
                    min={1}
                    max={20}
                    step={0.5}
                  />
                </div>
              </div>

              <div className="sip-cta-row">
                <Link to="/mutual-fund" className="sip-btn sip-btn--primary">
                  Explore Mutual Funds
                </Link>
                <Link to="/contact" className="sip-btn sip-btn--ghost">
                  Talk to an Advisor
                </Link>
              </div>
            </div>

            <div className="sip-panel sip-panel--results">
              <h2 className="sip-panel-title">Retirement Projection</h2>

              {results ? (
                <>
                  <div className="sip-results-top">
                    <div className="sip-donut-wrap">
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    <div className="sip-stats">
                      <div className="sip-stat">
                        <span className="sip-stat-label">
                          Investments at Retirement
                        </span>
                        <strong>
                          ₹{formatINR(results.futureValueInvestments)}
                        </strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">
                          Corpus to Accumulate
                        </span>
                        <strong className="sip-stat--teal">
                          ₹{formatINR(results.corpusToAccumulate)}
                        </strong>
                      </div>
                      <div className="sip-stat">
                        <span className="sip-stat-label">
                          Total Corpus Needed
                        </span>
                        <strong>₹{formatINR(results.requiredCorpus)}</strong>
                      </div>
                      <div className="sip-stat sip-stat--total">
                        <span className="sip-stat-label">
                          Required Monthly SIP
                        </span>
                        <strong>
                          ₹{formatINR(results.monthlyInvestment)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <p className="sip-summary">
                    With <strong>{results.yearsToRetirement} years</strong> to
                    retirement, you may need a corpus of{" "}
                    <strong>₹{formatINR(results.requiredCorpus)}</strong>. A
                    monthly SIP of{" "}
                    <strong>₹{formatINR(results.monthlyInvestment)}</strong>{" "}
                    can help bridge the gap after accounting for your existing
                    investments.
                  </p>
                </>
              ) : (
                <p className="sip-empty">
                  Enter a valid current age and retirement age to see your
                  projection.
                </p>
              )}
            </div>
          </div>

          {results && results.growthData.length > 0 && (
            <div className="sip-panel sip-panel--chart">
              <div className="sip-chart-head">
                <h2 className="sip-panel-title">Accumulation Path</h2>
                <p>Projected invested amount vs returns until retirement</p>
              </div>
              <div className="sip-bar-wrap">
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          )}

          <div className="sip-related">
            <p>Try our other calculators</p>
            <div className="sip-related-links">
              <Link to="/SipCalculator">SIP</Link>
              <Link to="/LumpsumCalculator">Lumpsum</Link>
              <Link to="/emi-calculator">EMI</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
